/**
 * React Router in data mode: loaders fetch before render, so a screen never
 * flashes an empty shell and every route knows its data up front.
 *
 * The role checks here are UX affordances only. The server re-derives scope on
 * every request; hiding a route is not access control.
 */
import { createBrowserRouter, redirect } from 'react-router';
import { ApiError, get, session } from './api.ts';
import { cachedGet } from './cache.ts';
import type {
  AdminAuditEntry,
  Article,
  ArticleDetail,
  GenerationContext,
  AdminClinicDetail,
  AdminClinicList,
  AdminOverview,
  AdminPlan,
  AdminStoreDetail,
  AdminPrompt,
  AdminPromptVersions,
  AdminUserList,
  AdminUserDetail,
  ActiveRulebook,
  CorpusRelease,
  RuleCandidate,
  RuleEvalCase,
  RuleSet,
  LegalSource,
  LegalSourceDetail,
  AdminStore,
  UsageResponse,
  DashboardData,
  CatalogItem,
  ListResponse,
  Member,
  MemberDetail,
  Session,
  Store,
  StoreDetail,
  UsageSummary,
} from './api.ts';
import AppShell from './components/AppShell.tsx';
import { AppSkeleton } from './components/Skeleton.tsx';
import Login from './routes/Login.tsx';
import ClinicDashboard from './routes/ClinicDashboard.tsx';
import Profile from './routes/Profile.tsx';
import Articles from './routes/Articles.tsx';
import ArticleEdit from './routes/ArticleEdit.tsx';
import ArticlePlan from './routes/ArticlePlan.tsx';
import ArticleWrite from './routes/ArticleWrite.tsx';
import ArticleFinal from './routes/ArticleFinal.tsx';
import ArticleNew from './routes/ArticleNew.tsx';
import Stores from './routes/Stores.tsx';
import StoreDetailPage from './routes/StoreDetail.tsx';
import Members from './routes/Members.tsx';
import MemberDetailPage from './routes/MemberDetail.tsx';
import Catalog from './routes/Catalog.tsx';
import Supervisors from './routes/Supervisors.tsx';
import Ctas from './routes/Ctas.tsx';
import Personas from './routes/Personas.tsx';
import ClinicSettings, { type ClinicSettingsData } from './routes/ClinicSettings.tsx';
import type { ClinicPromptStyles } from './components/PromptStyle.tsx';
import type { ClinicPromptStyleData } from './components/ClinicPromptEditor.tsx';
import { loadClinicFacts } from './components/ClinicFacts.tsx';
import type { AllowedHost } from './components/ClinicAllowedHosts.tsx';
import AdminDashboard from './routes/AdminDashboard.tsx';
import AdminUsers from './routes/AdminUsers.tsx';
import AdminUserDetailPage from './routes/AdminUserDetail.tsx';
import AdminClinicDetailPage from './routes/AdminClinicDetail.tsx';
import AdminAudit from './routes/AdminAudit.tsx';
import AdminSettings, { type PlatformSettings } from './routes/AdminSettings.tsx';
import type { AiModelList } from './components/ModelSettings.tsx';
import AdminStoreDetailPage from './routes/AdminStoreDetail.tsx';
import AdminClinicNew from './routes/AdminClinicNew.tsx';
import AdminTenants from './routes/AdminTenants.tsx';
import AdminPrompts from './routes/AdminPrompts.tsx';
import AdminDefaults from './routes/AdminDefaults.tsx';
import AdminPromptChecks, { type PromptChecksData } from './routes/AdminPromptChecks.tsx';
import type { PlatformDefaultItem } from './api.ts';
import type { EditorialCheckRow } from './editorial.ts';
import AdminPromptDetail from './routes/AdminPromptDetail.tsx';
import AdminPromptNew, { type PromptDefault } from './routes/AdminPromptNew.tsx';
import AdminLegal from './routes/AdminLegal.tsx';
import AdminLegalSource from './routes/AdminLegalSource.tsx';
import AdminRulebook from './routes/AdminRulebook.tsx';
import AdminAnalytics from './routes/AdminAnalytics.tsx';
import ErrorScreen from './routes/ErrorScreen.tsx';

async function requireSession({ request }: { request: Request }): Promise<Session> {
  try {
    const current = await session();
    /**
     * A JMC operator holds no clinic membership by design, so the clinic
     * dashboard has nothing it can load for them — send them to their own
     * screens instead of a page of empty panels.
     */
    if (
      new URL(request.url).pathname === '/' &&
      current.memberships.length === 0 &&
      current.user.platformRole === 'JMC_ADMIN'
    ) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect('/admin');
    }
    return current;
  } catch (err) {
    if (err instanceof Response) throw err;
    if (err instanceof ApiError && err.status === 401) {
      /*
       * Ask once more before throwing the person out.
       *
       * A 401 usually means the session is genuinely gone, but not always: a
       * login or a re-authentication in another tab replaces the cookie, and a
       * request that was already in flight when that happened comes back 401
       * holding a token that has just been superseded. The retry carries
       * whatever cookie the browser has now, so a session that is actually fine
       * survives - and one that is not fails the same way twice, at the cost of
       * a single request.
       */
      try {
        return await session(true);
      } catch (retry) {
        if (retry instanceof ApiError && retry.status === 401) {
          // Come back to the screen they were on, not to a bare dashboard.
          const back = new URL(request.url).pathname;
          const to = back === '/' ? '/login' : `/login?redirect_to=${encodeURIComponent(back)}`;
          // React Router signals a redirect by throwing a Response; that is its
          // API, not an error thrown by mistake.
          // eslint-disable-next-line @typescript-eslint/only-throw-error
          throw redirect(to);
        }
        throw retry;
      }
    }
    throw err;
  }
}

/**
 * The session, for a loader that needs the selected store and nothing else.
 *
 * Swallows the failure: the root loader has already redirected an unauthenticated
 * caller, so the only way this rejects here is a request that raced a sign-out -
 * and a screen that renders unfiltered is a better answer than one that errors.
 */
const sessionFor = async (): Promise<Session | null> => session().catch(() => null);

/** A screen whose data needs a clinic renders empty rather than erroring. */
const orEmpty = <T,>(promise: Promise<T>, fallback: T): Promise<T> =>
  promise.catch((err: unknown) => {
    if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return fallback;
    throw err;
  });

/**
 * A tenant screen asks for nothing until a clinic is actually selected.
 *
 * `orEmpty` alone was too late: the request had already been made, refused, and
 * written to the audit trail as a denied access. A JMC operator holds no
 * membership by design, so every page they opened filed one - and an audit log
 * full of denials that mean nothing is how a real one gets missed.
 *
 * The check reads the same session the root loader is fetching, so it costs no
 * extra request.
 */
const withClinic = async <T,>(load: () => Promise<T>, fallback: T): Promise<T> => {
  const { activeClinicId } = await session();
  return activeClinicId ? orEmpty(load(), fallback) : fallback;
};

const emptyList = <T,>(): ListResponse<T> => ({ items: [] });

/**
 * The article, its material, and everything it could be attached to.
 *
 * Shared by the plan screen and the editor: both decide the same four
 * references, so both need the same eligible sets, and a second copy of this
 * would be a second place for the scope rule to drift.
 */
const articleWithMaterial = async ({ params }: { params: { id?: string } }) => {
  /*
   * Two rounds, not seven one-after-another.
   *
   * This used to `await` article, then context, then four material lists, then
   * editorialChecks - seven round trips end to end for one screen, which is
   * why opening an article from anywhere else in the app sat in its own
   * loading state long enough for `usePendingScreen` on the PREVIOUS screen to
   * flash a skeleton before this one ever painted. Everything in round one
   * needs nothing but `params.id`; everything in round two needs round one's
   * `context` and `article` but nothing from each other.
   */
  const [article, context, editorialChecks] = await Promise.all([
    cachedGet<ArticleDetail>(`/articles/${params.id!}`, ['articles']),
    // Every approved fact the generator may draw on, so every fact table has
    // to be able to invalidate it.
    cachedGet<GenerationContext>(`/articles/${params.id!}/context`, [
      'articles',
      'personas',
      'treatments',
      'supervisors',
      'cta_templates',
      'strengths',
      'clinic_settings',
      'store_settings',
      'stores',
    ]),
    // 文章チェック's own patterns - see migration 096. No default: an article
    // rendered before this resolves would otherwise run the hardcoded list
    // this migration exists to remove.
    cachedGet<{ items: EditorialCheckRow[] }>('/editorial-checks', ['editorial_checks']),
  ]);

  /*
   * The material this article may be *attached to*, which is a different
   * question from the material it currently is: the context reports the
   * four chosen facts, and the editor has to offer every other one that
   * is eligible.
   *
   * Scoped exactly as articles/context.ts scopes its own reads - this
   * store, plus the clinic's shared rows only when the store's
   * `use_clinic_facts` is on - and APPROVED only. Offering a choice the
   * generator would then report as missing is the bug this mirrors away.
   */
  const scope = context.resolved.useClinicFacts ? 'effective' : 'store';
  const query = `?storeId=${article.store_id}&status=APPROVED&scope=${scope}`;
  const options = async (resource: string, table: string) =>
    withClinic(
      () => cachedGet<ListResponse<CatalogItem>>(`/${resource}${query}`, [table, 'stores']),
      emptyList(),
    );

  const [personas, treatments, supervisors, ctaTemplates] = await Promise.all([
    options('personas', 'personas'),
    options('treatments', 'treatments'),
    options('supervisors', 'supervisors'),
    options('cta-templates', 'cta_templates'),
  ]);

  return { article, context, personas, treatments, supervisors, ctaTemplates, editorialChecks };
};

export const router = createBrowserRouter([
  { path: '/login', element: <Login />, errorElement: <ErrorScreen /> },
  {
    id: 'root',
    element: <AppShell />,
    errorElement: <ErrorScreen />,
    // Cold start only: the session has not resolved yet, so there is genuinely
    // nothing to render. Later navigations keep the previous screen instead.
    HydrateFallback: AppSkeleton,
    loader: requireSession,
    children: [
      {
        index: true,
        element: <ClinicDashboard />,
        /*
         * Two reads, both cached and both invalidated by their own tables, so a
         * new article refreshes the dashboard without touching the quota figures
         * and a settled reservation does not re-fetch the article list.
         */
        /*
         * Both in parallel: two unrelated reads sent as one round trip's worth
         * of latency instead of two, back to back, for no reason - the pattern
         * this router had everywhere until it was measured as the reason a
         * navigation away from this screen visibly sat in its own loading state
         * before the next one appeared.
         */
        loader: async () => {
          const [usage, dashboard] = await Promise.all([
            withClinic<UsageSummary | null>(
              /*
               * quota_reservations and plan_limits also feed this, and neither
               * announces: the reservation ledger settles into the usage period
               * row (which does announce), and plan limits change on a release,
               * not while someone is looking at the screen.
               */
              () =>
                cachedGet<UsageSummary>('/usage/summary', [
                  'clinic_usage_periods',
                  'subscriptions',
                ]),
              null,
            ),
            withClinic<DashboardData | null>(
              () =>
                cachedGet<DashboardData>('/dashboard', [
                  'articles',
                  'stores',
                  'personas',
                  'treatments',
                  'supervisors',
                  'cta_templates',
                  'strengths',
                ]),
              null,
            ),
          ]);
          return { usage, dashboard };
        },
      },
      {
        // No loader: the shell already holds the session this renders from.
        path: 'profile',
        element: <Profile />,
      },
      {
        path: 'articles',
        element: <Articles />,
        // Filters ride in the URL so a filtered list is shareable and survives a
        // reload. The server decides which stores are in the list at all.
        loader: ({ request }) => {
          const url = new URL(request.url);
          const query = new URLSearchParams();
          for (const key of ['q', 'status', 'stage', 'storeId'] as const) {
            const value = url.searchParams.get(key);
            if (value) query.set(key, value);
          }
          const suffix = query.toString();
          return withClinic(
            () =>
              cachedGet<ListResponse<Article>>(`/articles${suffix ? `?${suffix}` : ''}`, [
                'articles',
                'stores',
              ]),
            emptyList(),
          );
        },
      },
      {
        path: 'articles/:id',
        element: <ArticleEdit />,
        loader: articleWithMaterial,
      },
      {
        /*
         * Step 2. Same loader as the editor - the plan screen needs the article,
         * the material it may be attached to, and the context that says what is
         * missing - so it is defined once and shared.
         */
        path: 'articles/:id/plan',
        element: <ArticlePlan />,
        loader: articleWithMaterial,
      },
      {
        /* Step 3. Same loader again - the screen shows the plan it writes to. */
        path: 'articles/:id/write',
        element: <ArticleWrite />,
        loader: articleWithMaterial,
      },
      {
        /* Step 6. The footer of every export comes from the same context. */
        path: 'articles/:id/final',
        element: <ArticleFinal />,
        loader: articleWithMaterial,
      },
      {
        path: 'articles/new',
        element: <ArticleNew />,
        /*
         * Eight independent reads, sent together - not the nine one-after-
         * another this used to be (the comment above them said "three", which
         * was wrong even when there were seven; nobody had counted). Sequential
         * `await`s here meant the wizard's own worst case: eight round trips
         * end to end before the first step could render anything, comfortably
         * past the delay that puts a loading skeleton on the PREVIOUS screen
         * first.
         *
         * Scoped to the selected store, which is a correctness fix rather than
         * a tidy-up: the wizard offered every store's personas and treatments,
         * and choosing one from another store produced an article whose
         * generation context reported the choice as *missing*. A screen must
         * not offer a choice the generator will refuse.
         */
        loader: async () => {
          const storeId = (await sessionFor())?.activeStoreId ?? '';
          const scoped = (path: string) => (storeId ? `${path}?storeId=${storeId}` : path);
          const [tones, articleTypes, personas, stores, treatments, strengths, supervisors, clinic, store] =
            await Promise.all([
              // The tone and article-type lists JMC keeps. Same source as the
              // clinic settings screen, so the two cannot drift apart again.
              withClinic(
                () =>
                  cachedGet<ListResponse<PlatformDefaultItem>>('/defaults?kind=TONE', [
                    'platform_defaults',
                  ]),
                emptyList<PlatformDefaultItem>(),
              ),
              withClinic(
                () =>
                  cachedGet<ListResponse<PlatformDefaultItem>>('/defaults?kind=ARTICLE_TYPE', [
                    'platform_defaults',
                  ]),
                emptyList<PlatformDefaultItem>(),
              ),
              withClinic(
                () =>
                  cachedGet<ListResponse<CatalogItem>>(scoped('/personas'), [
                    'personas',
                    'stores',
                  ]),
                emptyList(),
              ),
              withClinic(() => cachedGet<ListResponse<Store>>('/stores', ['stores']), emptyList()),
              withClinic(
                () =>
                  cachedGet<ListResponse<CatalogItem>>(scoped('/treatments'), [
                    'treatments',
                    'stores',
                  ]),
                emptyList(),
              ),
              /*
               * The material that used to be sent wholesale, so the writer can
               * choose which of it this article is written from. Same scoping
               * as the two above: a screen must not offer what the generator
               * would refuse.
               */
              withClinic(
                () =>
                  cachedGet<ListResponse<CatalogItem>>(scoped('/strengths'), [
                    'strengths',
                    'stores',
                  ]),
                emptyList(),
              ),
              withClinic(
                () =>
                  cachedGet<ListResponse<CatalogItem>>(scoped('/supervisors'), [
                    'supervisors',
                    'stores',
                  ]),
                emptyList(),
              ),
              /*
               * The facts the article may state, so step 1 can show them before
               * anything is generated. The clinic's settings carry the standing
               * tone and the standing length; the store's row carries the
               * region, the reservation URL and the opening hours.
               */
              withClinic(
                () =>
                  cachedGet<ClinicSettingsData>('/clinic', [
                    'clinics',
                    'clinic_settings',
                    'subscriptions',
                  ]),
                null,
              ),
              storeId
                ? orEmpty(
                    cachedGet<StoreDetail>(`/stores/${storeId}`, ['stores', 'store_settings']),
                    null,
                  )
                : Promise.resolve(null),
            ]);
          return {
            tones,
            articleTypes,
            personas,
            stores,
            treatments,
            strengths,
            supervisors,
            clinic,
            store,
          };
        },
      },
      {
        // Its own screen rather than the generic fact table: a persona is a
        // person, and the card list is how somebody picks one.
        path: 'personas',
        element: <Personas />,
        // Same shape as the catalog screens: the store comes from the session,
        // the scope filter from the URL.
        loader: async ({ request }) => {
          const scope = new URL(request.url).searchParams.get('scope') ?? '';
          const storeId = (await sessionFor())?.activeStoreId ?? '';
          const query = new URLSearchParams();
          if (storeId) query.set('storeId', storeId);
          if (scope) query.set('scope', scope);
          query.set('limit', '100');
          const suffix = query.toString();

          /*
           * The clinic's own personas, and JMC's suggestions beside them - in
           * parallel. Two reads because they are two different things: one is
           * the clinic's data, the other is a shared library the clinic copies
           * from; there was never a reason for the second to wait on the first.
           */
          return withClinic(
            async () => {
              const [personas, suggestions] = await Promise.all([
                cachedGet<ListResponse<CatalogItem>>(`/personas${suffix ? `?${suffix}` : ''}`, [
                  'personas',
                  'stores',
                ]),
                orEmpty(
                  cachedGet<ListResponse<PlatformDefaultItem>>('/defaults?kind=PERSONA', [
                    'platform_defaults',
                  ]),
                  emptyList<PlatformDefaultItem>(),
                ),
              ]);
              return { ...personas, suggestions };
            },
            { items: [], suggestions: emptyList<PlatformDefaultItem>() },
          );
        },
      },
      {
        /*
         * Before the generic catalog route, and deliberately on the same URL the
         * navigation already used: a supervisor is a person, and the card list is
         * how somebody picks one. Same loader shape as personas.
         */
        path: 'catalog/supervisors',
        element: <Supervisors />,
        loader: async ({ request }) => {
          const scope = new URL(request.url).searchParams.get('scope') ?? '';
          const storeId = (await sessionFor())?.activeStoreId ?? '';
          const query = new URLSearchParams();
          if (storeId) query.set('storeId', storeId);
          if (scope) query.set('scope', scope);
          query.set('limit', '100');
          const suffix = query.toString();

          return withClinic(
            () =>
              cachedGet<ListResponse<CatalogItem>>(`/supervisors${suffix ? `?${suffix}` : ''}`, [
                'supervisors',
                'stores',
              ]),
            emptyList(),
          );
        },
      },
      {
        // Its own screen for the same reason 監修医 has one: a CTA is a piece of
        // the published page, and the card is the only useful view of it.
        path: 'catalog/cta-templates',
        element: <Ctas />,
        loader: async ({ request }) => {
          const scope = new URL(request.url).searchParams.get('scope') ?? '';
          const storeId = (await sessionFor())?.activeStoreId ?? '';
          const query = new URLSearchParams();
          if (storeId) query.set('storeId', storeId);
          if (scope) query.set('scope', scope);
          query.set('limit', '100');
          const suffix = query.toString();

          return withClinic(
            () =>
              cachedGet<ListResponse<CatalogItem>>(`/cta-templates${suffix ? `?${suffix}` : ''}`, [
                'cta_templates',
                'stores',
              ]),
            emptyList(),
          );
        },
      },
      {
        path: 'catalog/:resource',
        element: <Catalog />,
        /*
         * The scope filter rides in the URL, like the article list's filters: a
         * filtered view is then shareable, survives a reload, and the back button
         * undoes it.
         *
         * The store comes from the session rather than the URL, because it is the
         * selector in the status strip - the same one that decides everything else
         * on screen. Picking a store there should narrow this list to that store,
         * which is what it did not do.
         */
        loader: async ({ params, request }) => {
          const url = new URL(request.url);
          const scope = url.searchParams.get('scope') ?? '';
          const session = await sessionFor();
          const storeId = session?.activeStoreId ?? '';

          const query = new URLSearchParams();
          if (storeId) query.set('storeId', storeId);
          if (scope) query.set('scope', scope);
          query.set('limit', '100');
          const suffix = query.toString();

          return {
            /*
              The slug and the table are the same word for every catalog resource.
              If that ever stops being true, cachedGet refuses to cache an unknown
              table rather than caching something nothing can invalidate.
            */
            ...(await withClinic(
              () =>
                cachedGet<ListResponse<unknown>>(
                  `/${params.resource!}${suffix ? `?${suffix}` : ''}`,
                  [params.resource!, 'stores'],
                ),
              emptyList(),
            )),
            resource: params.resource!,
          };
        },
      },
      {
        /*
         * The clinic's own settings screen. `PATCH /clinic/settings` was
         * clinic-admin-guarded from the start and had no caller in the browser,
         * so a clinic could not change its own site name or tone.
         */
        path: 'settings',
        element: <ClinicSettings />,
        loader: async () => {
          const [tones, clinic, prompts, allowedHosts] = await Promise.all([
            // The tone list JMC keeps, minus anything this clinic hid. It was
            // three values hardcoded here and three more, differently worded,
            // in ArticleNew - see AdminDefaults.
            orEmpty(
              cachedGet<ListResponse<PlatformDefaultItem>>('/defaults?kind=TONE', [
                'platform_defaults',
              ]),
              emptyList<PlatformDefaultItem>(),
            ),
            cachedGet<ClinicSettingsData>('/clinic', ['clinics', 'clinic_settings', 'subscriptions']),
            /*
             * The clinic's own way of writing, beside its settings: both are
             * "how our articles come out", and splitting them across two
             * screens meant nobody found the second one.
             */
            orEmpty(get<ClinicPromptStyles>('/clinic/prompts'), { items: [], requests: [] }),
            /*
             * The domains JMC has opened up for this clinic, read-only. Without
             * it a refused URL is 「許可されたドメインではありません」 with no way to
             * find out what IS allowed, and the answer arrives as a support
             * ticket instead of on the screen.
             */
            orEmpty(get<{ items: AllowedHost[] }>('/allowed-hosts'), {
              items: [] as AllowedHost[],
            }),
          ]);
          return { tones, clinic, prompts, allowedHosts };
        },
      },
      {
        path: 'stores',
        element: <Stores />,
        loader: () =>
          withClinic(() => cachedGet<ListResponse<Store>>('/stores', ['stores']), emptyList()),
      },
      {
        path: 'stores/:id',
        element: <StoreDetailPage />,
        // No withClinic fallback: this screen has no meaningful empty state, so
        // reaching it without a clinic is a genuine 403 for the error boundary
        // rather than a page rendered from nothing.
        loader: ({ params }) =>
          cachedGet<StoreDetail>(`/stores/${params.id!}`, [
            'stores',
            'store_settings',
            'clinic_settings',
            'store_memberships',
            'treatments',
            'personas',
            'supervisors',
            'cta_templates',
            'strengths',
          ]),
      },
      {
        path: 'members/:id',
        element: <MemberDetailPage />,
        // `users` supplies the name and last login and never announces, so the
        // membership tables are what invalidate this.
        loader: ({ params }) =>
          cachedGet<MemberDetail>(`/members/${params.id!}`, [
            'clinic_memberships',
            'store_memberships',
            'stores',
          ]),
      },
      {
        path: 'members',
        element: <Members />,
        loader: () =>
          withClinic(
            // `users` supplies the display name and never announces, so a
            // person renaming themselves shows on the next real change here.
            () =>
              // 100, not the default 25: this is a clinic's own staff, the
              // screen filters and searches over what it has, and a second page
              // for a list of eleven people is a scroll nobody expects.
              cachedGet<ListResponse<Member>>('/members?limit=100', [
                'clinic_memberships',
                'store_memberships',
                'stores',
              ]),
            emptyList(),
          ),
      },

      // --- JMC platform screens ---------------------------------------------
      {
        path: 'admin',
        element: <AdminDashboard />,
        // One request: counts, recent registrations, follow-ups and activity.
        // Not cached: the activity strip reads audit_events, which deliberately
        // has no change trigger, so nothing could ever invalidate this.
        loader: () => get<AdminOverview>('/admin/overview'),
      },
      {
        path: 'admin/users',
        element: <AdminUsers />,
        /*
         * Filtering happens server-side and the URL carries it, so a filtered
         * view is shareable and survives a reload.
         *
         * The clinic and store lists come along because every dialog on this
         * screen needs them: a membership cannot be granted without naming a
         * clinic, and a store grant cannot be moved without its siblings. They
         * are cached and change rarely, so this is one request each on a cold
         * screen and none after.
         */
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const query = new URLSearchParams();
          for (const key of ['q', 'status', 'role', 'clinicId'] as const) {
            const value = url.searchParams.get(key);
            if (value) query.set(key, value);
          }
          const [users, clinics, stores] = await Promise.all([
            /*
             * Cached and paged. `users` announces its changes since migration
             * 075, so a revisit costs nothing and a change anywhere - a
             * membership granted on another screen, a suspension - invalidates
             * it through the same stream everything else uses.
             */
            orEmpty(
              cachedGet<AdminUserList>(
                `/admin/users?${new URLSearchParams({ ...Object.fromEntries(query), limit: '10' })}`,
                ['users', 'clinic_memberships', 'store_memberships'],
              ),
              { items: [], facets: [], total: 0 },
            ),
            orEmpty(
              cachedGet<AdminClinicList>('/admin/clinics?limit=100', ['clinics', 'subscriptions']),
              { items: [], facets: [], total: 0 },
            ),
            orEmpty(
              cachedGet<ListResponse<AdminStore>>('/admin/stores?limit=100', ['stores', 'clinics']),
              emptyList(),
            ),
          ]);
          return { users, clinics, stores };
        },
      },
      {
        path: 'admin/users/:id',
        element: <AdminUserDetailPage />,
        // The user is not cached: `users` carries no clinic_id and so has no
        // change trigger, which means nothing could ever invalidate it. The
        // clinic and store lists are, and they are here because granting or
        // moving a membership needs both.
        loader: async ({ params }) => {
          const [user, clinics, stores] = await Promise.all([
            get<AdminUserDetail>(`/admin/users/${params.id!}`),
            orEmpty(
              cachedGet<AdminClinicList>('/admin/clinics?limit=100', ['clinics', 'subscriptions']),
              { items: [], facets: [], total: 0 },
            ),
            orEmpty(
              cachedGet<ListResponse<AdminStore>>('/admin/stores?limit=100', ['stores', 'clinics']),
              emptyList(),
            ),
          ]);
          return { user, clinics, stores };
        },
      },
      {
        path: 'admin/clinics/new',
        element: <AdminClinicNew />,
        // The active catalogue, so registration can offer the plan rather than
        // assign one and leave it to be corrected on the next screen.
        loader: async () => ({
          plans: await orEmpty(get<ListResponse<AdminPlan>>('/admin/plans'), emptyList()),
        }),
      },
      {
        path: 'admin/clinics/:id',
        element: <AdminClinicDetailPage />,
        // The plan list rides along so the edit panel can offer it without a
        // second round trip when the panel opens - and it is fetched alongside
        // the clinic, not after it, since neither depends on the other.
        loader: async ({ params }) => {
          const [detail, plans, promptStyles, facts, allowedHosts] = await Promise.all([
            get<AdminClinicDetail>(`/admin/clinics/${params.id!}`),
            orEmpty(get<ListResponse<AdminPlan>>('/admin/plans'), emptyList()),
            // The wording written for this clinic, and the requests behind it.
            get<ClinicPromptStyleData>(`/admin/clinics/${params.id!}/prompt-styles`),
            // The clinic's material, through the JMC mount of the clinic's own
            // routes - the same rows its own screens list. See ClinicFacts.
            loadClinicFacts(params.id!, get),
            /*
             * The domains this clinic may have read. A plain get, not cachedGet
             * and not orEmpty: the loader blocks on the four above anyway so a
             * cache entry buys nothing, and rendering an empty list after a
             * FAILED fetch would invite an operator to re-add rows that are
             * already there.
             */
            get<{ items: AllowedHost[] }>(`/admin/clinics/${params.id!}/allowed-hosts`),
          ]);
          return { ...detail, plans, promptStyles, facts, allowedHosts };
        },
      },
      {
        path: 'admin/stores/:id',
        element: <AdminStoreDetailPage />,
        loader: async ({ params }) => {
          const store = await cachedGet<AdminStoreDetail>(`/admin/stores/${params.id!}`, [
            'stores',
            'store_settings',
            'store_memberships',
          ]);
          // Both need the clinic id the store fetch just returned, but not
          // each other - parallel from here rather than sequential.
          const [material, siblingStores] = await Promise.all([
            /*
             * The clinic's material, narrowed to this store by the panel: a
             * store's own rows plus the clinic-wide ones, which is exactly what
             * this store's generations can draw on.
             */
            loadClinicFacts(store.store.clinic_id, get),
            /*
             * Every store of this clinic, not just this one. The member editor
             * on this page can move somebody to another branch, and the branch
             * they are being moved to is by definition not the one being
             * looked at.
             */
            cachedGet<ListResponse<AdminStore>>(
              `/admin/stores?clinicId=${store.store.clinic_id}&limit=100`,
              ['stores', 'clinics'],
            ),
          ]);
          return { ...store, material, siblingStores };
        },
      },
      {
        path: 'admin/audit',
        element: <AdminAudit />,
        // Filters live in the URL so a view can be shared with a colleague.
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const query = new URLSearchParams();
          for (const key of ['clinicId', 'storeId', 'action', 'outcome'] as const) {
            const value = url.searchParams.get(key);
            if (value) query.set(key, value);
          }
          const suffix = query.toString();
          const clinicId = url.searchParams.get('clinicId');
          /*
           * The two pickers' options, beside the log - in parallel, and the
           * stores only once a clinic is chosen (a store list across every
           * clinic would be a list nobody could read).
           */
          const [log, clinics, stores] = await Promise.all([
            orEmpty(
              get<ListResponse<AdminAuditEntry>>(`/admin/audit${suffix ? `?${suffix}` : ''}`),
              emptyList<AdminAuditEntry>(),
            ),
            orEmpty(
              cachedGet<AdminClinicList>('/admin/clinics?limit=100', ['clinics', 'subscriptions']),
              { ...emptyList<AdminClinicList['items'][number]>(), facets: [], total: 0 },
            ),
            clinicId
              ? orEmpty(
                  get<ListResponse<AdminStore>>(`/admin/stores?clinicId=${clinicId}&limit=100`),
                  emptyList<AdminStore>(),
                )
              : Promise.resolve(emptyList<AdminStore>()),
          ]);
          return { ...log, clinics: clinics.items, stores: stores.items };
        },
      },
      {
        path: 'admin/tenants',
        element: <AdminTenants />,
        /*
         * Search, status, plan and sort all run on the server, and only against
         * the tab being looked at: a clinic search that also thinned the store
         * list would silently empty the store tab beside it.
         *
         * The first ten rows of each tab. The rest arrive from the screen, as
         * the reader reaches them - see paging.ts. Both tabs are loaded because
         * switching tabs must not cost a round trip.
         */
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const storesTab = url.searchParams.get('tab') === 'stores';
          const query = (keys: readonly string[]) => {
            const params = new URLSearchParams({ limit: '10' });
            for (const key of keys) {
              const value = url.searchParams.get(key);
              if (value) params.set(key, value);
            }
            return `?${params.toString()}`;
          };
          const [clinics, stores] = await Promise.all([
            orEmpty(
              cachedGet<AdminClinicList>(
                `/admin/clinics${query(storesTab ? ['sort'] : ['q', 'status', 'plan', 'sort'])}`,
                ['clinics', 'subscriptions'],
              ),
              { items: [], facets: [], total: 0 },
            ),
            orEmpty(
              cachedGet<ListResponse<AdminStore>>(
                `/admin/stores${query(storesTab ? ['q', 'status'] : [])}`,
                ['stores', 'clinics'],
              ),
              emptyList(),
            ),
          ]);
          return { clinics, stores };
        },
        /*
         * The tab lives in the URL for shareable links, but both lists are
         * already loaded — so switching tabs must not re-hit the server. Without
         * this, every tab click is a navigation with a loading state, and the old
         * panel lingers until the round trip finishes.
         *
         * A filter change is the opposite case: the same pathname, but the rows
         * have to come from the server, so `tab` is the one parameter compared
         * away here.
         */
        shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) => {
          if (currentUrl.pathname !== nextUrl.pathname) return defaultShouldRevalidate;
          const filters = (url: URL) => {
            const params = new URLSearchParams(url.search);
            params.delete('tab');
            params.sort();
            return params.toString();
          };
          return filters(currentUrl) !== filters(nextUrl);
        },
      },
      {
        path: 'admin/prompts',
        element: <AdminPrompts />,
        loader: () =>
          orEmpty(
            cachedGet<ListResponse<AdminPrompt>>('/admin/prompts', ['prompts', 'prompt_versions']),
            emptyList(),
          ),
      },
      {
        /*
         * Before ':id', or a new prompt would be looked up as one whose id is
         * the word "new".
         */
        path: 'admin/prompts/new',
        element: <AdminPromptNew />,
        // The wording this build ships, which is what a new prompt starts from.
        loader: async () => ({
          defaults: await get<ListResponse<PromptDefault>>('/admin/prompt-defaults'),
        }),
      },
      {
        path: 'admin/prompts/:id',
        element: <AdminPromptDetail />,
        /*
         * Not cached. The versions come back with the current lint verdict
         * attached, which depends on the rules in this build rather than only on
         * the rows - so a cached copy could show a release as permitted after a
         * deploy that stopped permitting it.
         */
        loader: async ({ params }) => ({
          versions: await get<AdminPromptVersions>(`/admin/prompts/${params.id!}/versions`),
        }),
      },
      {
        /*
         * The checks the prompt editor runs. Its own screen rather than a panel
         * on the prompt page: what a check says applies to every prompt, and
         * editing one while looking at a single instruction invites writing the
         * check around that instruction.
         *
         * Cached against its own table, which announces changes (migration 095).
         */
        path: 'admin/prompt-checks',
        element: <AdminPromptChecks />,
        loader: async () => {
          const [prompts, editorial] = await Promise.all([
            cachedGet<PromptChecksData>('/admin/prompt-checks', ['prompt_checks']),
            // The 文章チェック panel's own patterns, same screen - see
            // migration 096 and the note on AdminPromptChecks.
            cachedGet<{ items: EditorialCheckRow[] }>('/admin/editorial-checks', [
              'editorial_checks',
            ]),
          ]);
          return { prompts, editorial };
        },
      },
      {
        // JMC's shared starting material. Its own screen rather than a section of
        // the prompt page: a prompt is versioned wording for the model, this is
        // content a clinic copies and edits, and the two have different states.
        path: 'admin/defaults',
        element: <AdminDefaults />,
        loader: () =>
          cachedGet<ListResponse<unknown>>('/admin/defaults', ['platform_defaults']),
      },
      {
        path: 'admin/legal',
        element: <AdminLegal />,
        /*
         * Cached, since migration 075 gave these tables change triggers.
         *
         * It deliberately was not: both lists change from the WORKER - a fetch
         * job lands a new version, an embedding pass empties the pending count -
         * and with nothing announcing that, a cached copy could not be
         * invalidated and a corpus screen showing yesterday's state is worse
         * than one that costs a round trip. Now the worker's write is what
         * refreshes the screen, which is better than either.
         */
        loader: async () => {
          const [sources, releases] = await Promise.all([
            cachedGet<ListResponse<LegalSource> & { allowedHosts: string[]; refetchDays: number }>(
              '/admin/legal/sources',
              ['legal_sources', 'legal_documents'],
            ),
            cachedGet<ListResponse<CorpusRelease>>('/admin/legal/releases', [
              'corpus_releases',
              'legal_documents',
            ]),
          ]);
          return { sources, releases };
        },
      },
      {
        path: 'admin/rulebook',
        element: <AdminRulebook />,
        /*
         * Not cached. The candidate queue changes when somebody presses 抽出, the
         * active set changes on activation, and neither table carries a clinic_id
         * so neither has a change stream that could invalidate a cached copy.
         */
        loader: async () => {
          const [sets, active, candidates, evalCases] = await Promise.all([
            get<ListResponse<RuleSet> & { pendingCandidates: number }>('/admin/rulebook/sets'),
            get<ActiveRulebook>('/admin/rulebook/active'),
            get<ListResponse<RuleCandidate>>('/admin/rulebook/candidates'),
            get<ListResponse<RuleEvalCase>>('/admin/rulebook/eval-cases'),
          ]);
          return { sets, active, candidates, evalCases };
        },
      },
      {
        path: 'admin/legal/sources/:id',
        element: <AdminLegalSource />,
        /*
         * Cached like the list beside it. The versions arrive from the WORKER,
         * so nothing the reader does would prompt a refetch - the change
         * triggers from migration 075 are what refresh this screen when a fetch
         * lands.
         */
        loader: ({ params }) =>
          cachedGet<LegalSourceDetail>(`/admin/legal/sources/${params.id!}`, [
            'legal_sources',
            'legal_documents',
          ]),
      },
      {
        path: 'admin/settings',
        element: <AdminSettings />,
        /*
         * What JMC decides about spend: the model and its rates, the plans sold
         * against them, and JMC's own ceiling.
         */
        loader: async () => {
          const [models, platform, plans] = await Promise.all([
            get<AiModelList>('/admin/ai-models'),
            get<PlatformSettings>('/admin/platform-settings'),
            // Inactive ones included: a retired plan still has clinics on it,
            // and hiding it would report the catalogue as smaller than it is.
            orEmpty(get<ListResponse<AdminPlan>>('/admin/plans?includeInactive=true'), emptyList()),
          ]);
          return { models, platform, plans };
        },
      },
      {
        path: 'admin/analytics',
        element: <AdminAnalytics />,
        loader: () =>
          orEmpty(
            cachedGet<UsageResponse>('/admin/usage', [
              'clinic_usage_periods',
              'clinics',
              'stores',
              'subscriptions',
            ]),
            { items: [], entities: [] },
          ),
      },
    ],
  },
]);
